"use strict";
/**
 * Ruby Tokens — the display unit for AI credits (spec 2026-09-27 D6/D7).
 *
 * 1 stored credit = 1,000 Ruby Tokens. Storage, tables and field names keep
 * using credits (backward-compat rule); only what a person reads is Ruby
 * Tokens. Never abbreviate the name to "RT".
 *
 * RUNTIME values — import by NAME from '@platform/shared-types' (they are
 * re-exported explicitly from the barrel so bundlers keep them).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ADDITIONAL_USAGE_CONSENT_VERSION = exports.DEFAULT_ADDITIONAL_USAGE_CAP = exports.ADDITIONAL_USAGE_CAP_OPTIONS = exports.RUBY_TOKEN_USAGE_CENTS_PER_CREDIT = exports.RUBY_TOKENS_PER_CREDIT = void 0;
exports.setDisplayTokensPerCredit = setDisplayTokensPerCredit;
exports.getDisplayTokensPerCredit = getDisplayTokensPerCredit;
exports.creditsToRubyTokens = creditsToRubyTokens;
exports.rubyTokensToCredits = rubyTokensToCredits;
exports.formatRubyTokens = formatRubyTokens;
exports.formatRubyTokensExact = formatRubyTokensExact;
exports.formatCreditsAsRubyTokens = formatCreditsAsRubyTokens;
exports.usageLineCents = usageLineCents;
exports.RUBY_TOKENS_PER_CREDIT = 1000;
/**
 * Live tokens-per-credit for DISPLAY (owner setting, Super Admin → AI →
 * Settings). Frontends set it from the server's `tokensPerCredit` once it
 * loads; until then — and always on the backend, which passes the value
 * explicitly — it is RUBY_TOKENS_PER_CREDIT.
 */
let displayTokensPerCredit = exports.RUBY_TOKENS_PER_CREDIT;
/** Set the live display rate; null restores the default. Invalid values are ignored. */
function setDisplayTokensPerCredit(n) {
    if (n === null || n === undefined) {
        displayTokensPerCredit = exports.RUBY_TOKENS_PER_CREDIT;
        return;
    }
    if (typeof n === 'number' && Number.isInteger(n) && n >= 1)
        displayTokensPerCredit = n;
}
function getDisplayTokensPerCredit() {
    return displayTokensPerCredit;
}
/**
 * Stored credits → Ruby Tokens (display). Non-finite input reads as 0.
 * `perCredit` defaults to the live display rate.
 */
function creditsToRubyTokens(credits, perCredit = displayTokensPerCredit) {
    if (!Number.isFinite(credits))
        return 0;
    return Math.round(credits * perCredit);
}
/**
 * Ruby Tokens → stored credits, rounded UP so a charge is never
 * under-collected (e.g. 1,001 tokens → 2 credits). Non-finite input reads as 0.
 * Backend callers pass the live `perCredit` from the CONFIG row explicitly.
 */
function rubyTokensToCredits(tokens, perCredit = displayTokensPerCredit) {
    if (!Number.isFinite(tokens))
        return 0;
    // Round away float noise first (e.g. 0.1 * 3 * 1000) before ceiling.
    const exact = Math.round((tokens / perCredit) * 1e9) / 1e9;
    return Math.ceil(exact);
}
const UNITS = [
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' },
];
function trimZeros(s) {
    return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}
/**
 * Abbreviated Ruby Token amount: 1_370_000 → "1.37M", 166_000 → "166K",
 * 6_000 → "6K", 12_500 → "12.5K", 950 → "950". Up to three significant
 * figures; trailing zeros dropped; a value that rounds up to the next unit is
 * promoted (999_999 → "1M"). Non-finite input → "0".
 */
function formatRubyTokens(tokens) {
    if (!Number.isFinite(tokens))
        return '0';
    const sign = tokens < 0 ? '-' : '';
    const n = Math.abs(tokens);
    if (n < 1000)
        return sign + String(Math.round(n));
    for (let i = 0; i < UNITS.length; i++) {
        const { value, suffix } = UNITS[i];
        if (n < value)
            continue;
        const scaled = n / value;
        const decimals = scaled < 10 ? 2 : scaled < 100 ? 1 : 0;
        const rounded = Number(scaled.toFixed(decimals));
        if (rounded >= 1000 && i > 0) {
            return sign + trimZeros((n / UNITS[i - 1].value).toFixed(2)) + UNITS[i - 1].suffix;
        }
        return sign + trimZeros(rounded.toFixed(decimals)) + suffix;
    }
    return sign + String(Math.round(n));
}
/** Exact Ruby Token amount with thousands separators: 1370000 → "1,370,000". */
function formatRubyTokensExact(tokens) {
    if (!Number.isFinite(tokens))
        return '0';
    return Math.round(tokens).toLocaleString('en-US');
}
/** Convenience: stored credits → abbreviated Ruby Tokens ("1.37M"). */
function formatCreditsAsRubyTokens(credits) {
    return formatRubyTokens(creditsToRubyTokens(credits));
}
/** Additional token usage (owner 2026-10-11): billed per credit on the subscription invoice. */
exports.RUBY_TOKEN_USAGE_CENTS_PER_CREDIT = 1;
/** Monthly limit choices, in CREDITS (2M / 5M / 10M Ruby Tokens / no limit). */
exports.ADDITIONAL_USAGE_CAP_OPTIONS = [2000, 5000, 10000, null];
exports.DEFAULT_ADDITIONAL_USAGE_CAP = 5000;
/** Bump when the checkout consent wording changes. */
exports.ADDITIONAL_USAGE_CONSENT_VERSION = '2026-10-11';
function usageLineCents(credits) {
    return Math.max(0, Math.round(credits * exports.RUBY_TOKEN_USAGE_CENTS_PER_CREDIT));
}
//# sourceMappingURL=rubyTokens.js.map